<div
  class="oauth-consent-container tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50 dark:tw-bg-gray-900">
  <div class="tw-w-full tw-max-w-md tw-px-4">
    <div *ngIf="loading" class="tw-text-center">
      <mat-spinner class="tw-mx-auto" diameter="40"></mat-spinner>
      <p class="tw-mt-4 tw-text-gray-600 dark:tw-text-gray-400">Loading application details...</p>
    </div>

    <app-alert alertType="error" *ngIf="error">
      <h3 class="tw-text-sm tw-font-medium">Authorization Error</h3>
      <p class="tw-mt-1 tw-text-sm">{{ error }}</p>
    </app-alert>

    <!-- Shown instead of the consent flow when the request comes from the deprecated MCP server -->
    <mat-card *ngIf="showMcpReconfigureWarning" class="tw-shadow-xl">
      <!-- Platform Logo -->
      <div class="tw-text-center tw-pt-6 tw-pb-4">
        <img
          class="tw-h-8 tw-mx-auto tw-mb-6 dark:tw-hidden"
          src="../../assets/img/logo-gray.svg"
          alt="GotLiveData logo" />
        <img
          class="tw-h-8 tw-mx-auto tw-mb-6 tw-hidden dark:tw-block"
          src="../../assets/img/logo-white.svg"
          alt="GotLiveData logo" />
      </div>

      <!-- Subtle divider -->
      <div class="tw-border-t tw-border-gray-200 dark:tw-border-gray-700 tw-mx-6"></div>

      <mat-card-header class="tw-text-center tw-pb-0 tw-pt-6">
        <div class="tw-w-full">
          <mat-icon class="tw-text-6xl tw-w-20 tw-h-20 tw-mx-auto tw-mb-4 tw-text-orange-500">
            warning
          </mat-icon>
          <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 dark:tw-text-gray-100">
            MCP Server Address Has Changed
          </h1>
          <p class="tw-text-sm tw-text-gray-600 dark:tw-text-gray-400 tw-mt-2">
            Your AI client is connecting to an outdated MCP server address
          </p>
        </div>
      </mat-card-header>

      <mat-card-content class="tw-pt-6">
        <app-alert alertType="warning">
          <p class="tw-text-sm">
            This connection can't be completed because it uses the old MCP server address. To
            continue, update the MCP server URL in your AI client's settings to point to
            <code>mcp.gotlivedata.com</code> instead of <code>mcp.workforce.ai</code>, then start
            the connection again.
          </p>
        </app-alert>

        <div class="tw-bg-gray-50 dark:tw-bg-gray-800 tw-rounded-lg tw-p-4 tw-mb-6">
          <div class="tw-flex tw-items-center tw-gap-3">
            <span
              class="tw-text-xs tw-font-medium tw-uppercase tw-w-14 tw-shrink-0 tw-text-gray-500 dark:tw-text-gray-400">
              Old
            </span>
            <code class="tw-text-sm tw-line-through tw-text-gray-500 dark:tw-text-gray-400">
              mcp.workforce.ai
            </code>
          </div>
          <div class="tw-flex tw-items-center tw-gap-3 tw-mt-2">
            <span
              class="tw-text-xs tw-font-medium tw-uppercase tw-w-14 tw-shrink-0 tw-text-gray-500 dark:tw-text-gray-400">
              New
            </span>
            <code class="tw-text-sm tw-font-semibold tw-text-gray-900 dark:tw-text-gray-100">
              mcp.gotlivedata.com
            </code>
          </div>
        </div>

        <p class="tw-text-sm tw-text-gray-600 dark:tw-text-gray-400">
          The exact steps vary by AI client — look for its MCP server, connector, or integration
          settings and replace the old address with the new one. If you need help, contact
          <a
            href="mailto:support@livedatatechnologies.com"
            class="tw-text-blue-600 dark:tw-text-blue-400">
            support&#64;livedatatechnologies.com</a
          >.
        </p>
      </mat-card-content>
    </mat-card>

    <mat-card
      *ngIf="!loading && !error && clientDetails && !showMcpReconfigureWarning"
      class="tw-shadow-xl">
      <!-- Platform Logo -->
      <div class="tw-text-center tw-pt-6 tw-pb-4">
        <img
          class="tw-h-8 tw-mx-auto tw-mb-6 dark:tw-hidden"
          src="../../assets/img/logo-gray.svg"
          alt="GotLiveData logo" />
        <img
          class="tw-h-8 tw-mx-auto tw-mb-6 tw-hidden dark:tw-block"
          src="../../assets/img/logo-white.svg"
          alt="GotLiveData logo" />
      </div>

      <!-- Subtle divider -->
      <div class="tw-border-t tw-border-gray-200 dark:tw-border-gray-700 tw-mx-6"></div>

      <mat-card-header class="tw-text-center tw-pb-0 tw-pt-6">
        <div class="tw-w-full">
          <mat-icon class="tw-text-6xl tw-w-20 tw-h-20 tw-mx-auto tw-mb-4 tw-text-gray-400">
            apps
          </mat-icon>
          <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 dark:tw-text-gray-100">
            {{ clientDetails.client_name }}
          </h1>
          <p class="tw-text-sm tw-text-gray-600 dark:tw-text-gray-400 tw-mt-2">
            wants to access your {{ isWorkforceLogin ? 'Workforce.ai' : 'GotLiveData' }} account
          </p>
        </div>
      </mat-card-header>

      <mat-card-content class="tw-pt-6">
        <!-- Organization Selection (Multi-org users only) -->
        <div *ngIf="showOrgSelection" class="tw-mb-6">
          <h3 class="tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-gray-100 tw-mb-3">
            Select Organization
          </h3>
          <div class="tw-bg-gray-50 dark:tw-bg-gray-800 tw-rounded-lg tw-p-4">
            <mat-radio-group [(ngModel)]="selectedOrgId" class="tw-flex tw-flex-col tw-gap-3">
              <mat-radio-button
                *ngFor="let org of orgs"
                [value]="org.id"
                class="tw-text-sm tw-text-gray-700 dark:tw-text-gray-300">
                <div class="tw-flex tw-items-center tw-gap-2">
                  <mat-icon class="tw-text-base tw-text-gray-500 dark:tw-text-gray-400">
                    corporate_fare
                  </mat-icon>
                  <span class="tw-font-medium">{{ org.name }}</span>
                </div>
              </mat-radio-button>
            </mat-radio-group>
          </div>
        </div>

        <div class="tw-mb-6">
          <h3 class="tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-gray-100 tw-mb-3">
            This application will be able to:
          </h3>
          <div class="tw-bg-gray-50 dark:tw-bg-gray-800 tw-rounded-lg tw-p-4">
            <ul class="tw-space-y-2">
              <li *ngFor="let scope of scopes" class="tw-flex tw-items-start">
                <mat-icon class="tw-text-green-500 tw-mr-2 tw-text-lg tw-mt-0.5"
                  >check_circle</mat-icon
                >
                <span class="tw-text-sm tw-text-gray-700 dark:tw-text-gray-300">
                  {{ getScopeDescription(scope) }}
                </span>
              </li>
            </ul>
            <div *ngIf="!scopes || scopes.length === 0">
              <p class="tw-text-sm tw-text-gray-700 dark:tw-text-gray-300">
                Act on your behalf in this account
              </p>
            </div>
          </div>
        </div>

        <app-alert alertType="info" alertStyle="compact">
          <span class="tw-text-xs">
            By authorizing, you allow this application to access your data according to the
            permissions above. You can revoke access at any time from your account settings.
          </span>
        </app-alert>
      </mat-card-content>

      <mat-card-actions class="tw-flex tw-gap-3 tw-px-4 tw-pb-4">
        <button
          mat-raised-button
          color="primary"
          (click)="authorize()"
          [disabled]="authorizing || !selectedOrgId"
          class="tw-flex-1">
          <mat-spinner
            *ngIf="authorizing"
            diameter="20"
            class="tw-inline-block tw-mr-2"></mat-spinner>
          <span *ngIf="!authorizing">Authorize</span>
          <span *ngIf="authorizing">Authorizing...</span>
        </button>
        <button mat-stroked-button (click)="deny()" [disabled]="authorizing" class="tw-flex-1">
          Deny
        </button>
      </mat-card-actions>
    </mat-card>
  </div>
</div>
